import React, { FC } from 'react';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface CompliancePartyStatus {
	isCompliant: boolean;
	canReceiveWorkOrders: boolean;
}

interface CompliancePartyBadgeProps {
	// Backend-owned party status for the row's supplier/client. Null while the
	// overview is still loading its status map — the badge renders nothing then.
	status: CompliancePartyStatus;
	// 'buyer' sees a supplier that "cannot receive" its work; 'supplier' sees a
	// client it "cannot send"/serve — the wording flips by perspective.
	perspective: 'buyer' | 'supplier';
	// Optional interactive control the caller renders in place of the static
	// work-order-block line (e.g. the buyer Overview's per-supplier "can/cannot
	// send work orders" override dropdown). When provided it replaces the default
	// line and is shown for every non-compliant party (regardless of the current
	// canReceiveWorkOrders value) so the state can always be toggled back.
	workOrderControl?: React.ReactNode;
}

const GREEN = 'rgb(27, 121, 34)';
const GREEN_BG = 'rgb(237, 247, 237)';
const RED = 'rgb(204, 50, 50)';
const RED_BG = 'rgb(255, 241, 240)';

const PILL_CLASS =
	'footnoteMedium inline-flex w-fit flex-row items-center gap-1 rounded px-2 py-0.5';

// Row-level Compliant / Non-Compliant tag for the overview party column. Driven
// entirely by the backend party-status endpoints (supplier_statuses /
// client_statuses): a filled pill (green when compliant, red when not) plus a
// work-order-block line beneath when the party cannot receive work orders.
export const CompliancePartyBadge: FC<CompliancePartyBadgeProps> = ({
	status,
	perspective,
	workOrderControl,
}) => {
	if (!status) {
		return null;
	}

	if (status.isCompliant) {
		return (
			<span className={PILL_CLASS} style={{ color: GREEN, backgroundColor: GREEN_BG }}>
				<CheckCircleOutlined />
				Compliant
			</span>
		);
	}

	const workOrderText =
		perspective === 'buyer' ? 'Cannot send work orders' : 'Cannot receive work orders';

	return (
		<div className="flex flex-col gap-1">
			<span className={PILL_CLASS} style={{ color: RED, backgroundColor: RED_BG }}>
				<ExclamationCircleOutlined />
				Non-Compliant
			</span>
			{workOrderControl !== undefined ? (
				workOrderControl
			) : !status.canReceiveWorkOrders ? (
				<span className="footnoteregular" style={{ color: RED }}>
					{workOrderText}
				</span>
			) : null}
		</div>
	);
};
